import React, { useState } from 'react';
import Icon from '../../../components/common/Icon/Icon';
import PhotoFullscreenModal from '../../../components/common/PhotoFullscreenModal/PhotoFullscreenModal';
import type { MealPhoto } from '../../../types/mealTracking';
import styles from './MealPhotoViewer.module.css';

interface MealPhotoViewerProps {
  photos: MealPhoto[];
  onAddPhoto?: () => void;
  showEmptyActions?: boolean;
}

const MealPhotoViewer: React.FC<MealPhotoViewerProps> = ({
  photos,
  onAddPhoto,
  showEmptyActions = false,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);

  const safeIndex = photos.length > 0 ? Math.min(activeIndex, photos.length - 1) : 0;
  const activePhoto = photos[safeIndex];

  const goPrevious = () => {
    setActiveIndex((current) => (current - 1 + photos.length) % photos.length);
  };

  const goNext = () => {
    setActiveIndex((current) => (current + 1) % photos.length);
  };

  if (!activePhoto) {
    return (
      <div className={styles.emptyViewer}>
        <Icon name="photo_camera" size={40} color="var(--color-text-muted)" />
        <p className={styles.emptyText}>Nenhuma foto adicionada a esta refeição.</p>
        {showEmptyActions && onAddPhoto && (
          <button type="button" className={styles.emptyAction} onClick={onAddPhoto}>
            Adicionar foto
          </button>
        )}
      </div>
    );
  }

  return (
    <>
      <div className={styles.viewer}>
        {photos.length > 1 && (
          <button type="button" className={`${styles.navButton} ${styles.navButtonLeft}`} onClick={goPrevious} aria-label="Foto anterior">
            <Icon name="chevron_left" size={24} color="var(--color-neutral-white)" />
          </button>
        )}

        <button
          type="button"
          className={styles.imageButton}
          onClick={() => setFullscreenOpen(true)}
          aria-label="Ampliar foto"
        >
          <img src={activePhoto.previewUrl} alt={activePhoto.fileName} className={styles.image} />
        </button>

        {photos.length > 1 && (
          <button type="button" className={`${styles.navButton} ${styles.navButtonRight}`} onClick={goNext} aria-label="Próxima foto">
            <Icon name="chevron_right" size={24} color="var(--color-neutral-white)" />
          </button>
        )}

        {photos.length > 1 && (
          <span className={styles.counter}>{safeIndex + 1}/{photos.length}</span>
        )}
      </div>

      <PhotoFullscreenModal
        open={fullscreenOpen}
        src={activePhoto.previewUrl}
        alt={activePhoto.fileName}
        onClose={() => setFullscreenOpen(false)}
      />
    </>
  );
};

export default MealPhotoViewer;
